import { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Receipt, Save, Loader2, CheckCircle2, Eye, Copy } from "lucide-react";
import { DOC_THEMES, DOC_TEMPLATES, DOC_CONTENT_DEFAULTS, docBrandName, docFontFamily } from "@/lib/company";
import { ThermalDoc, THERMAL_PREVIEW_CSS } from "@/components/ThermalReceipt";
import { InvoiceA5Doc, INVOICE_A5_CSS } from "@/components/InvoiceA5";
import { FinanceDocA5, FINANCE_DOC_CSS } from "@/components/FinanceDocs";
import { buildDispatchHTML } from "@/lib/dispatch";
import { renderToStaticMarkup } from "react-dom/server";
import { fileUrl } from "@/components/EntityAvatar";

const DOC_TYPE_META = [
  ["receipt", "Fiş (+Z-Rapor)"],
  ["invoice", "Fatura"],
  ["dispatch", "İrsaliye / Sevk"],
  ["finance", "Makbuz / Dekont"],
];
const DOC_DEFAULT_TITLE = { receipt: "SATIŞ FİŞİ", invoice: "SATIŞ FATURASI", dispatch: "TRANSFER / SEVK İRSALİYESİ", finance: "TAHSİLAT MAKBUZU" };

const _now = () => new Date().toISOString();
const SAMPLE_SALE = {
  sale_no: "GM-ORNEK-0001", created_at: _now(), location_name: "Merkez Şube",
  lines: [
    { name: "Örnek Ürün A", quantity: 2, unit_price: 24.9, vat_rate: 20, line_total: 49.8 },
    { name: "Örnek Ürün B", quantity: 1, unit_price: 15.0, vat_rate: 10, line_total: 15.0 },
  ],
  subtotal: 56.7, vat_total: 8.1, discount: 0, total: 64.8,
  payment: { method: "cash", tenders: [{ method: "cash", amount: 64.8 }] }, change_try: 0,
};
const SAMPLE_ENTRY = {
  kind: "income", doc_no: "MKB-ORNEK-0001", date: _now().slice(0, 10), account: "cash",
  cari_name: "Örnek Müşteri Ltd. Şti.", location_name: "Merkez Şube",
  description: "Örnek tahsilat bedeli", category: "Satış", amount: 100.0, vat_rate: 20, vat_amount: 20.0, gross: 120.0,
};
const SAMPLE_ORDER = {
  transfer_no: "SVK-ORNEK-0001", order_type: "transfer", status: "approved", created_at: _now(),
  from_location_name: "Merkez Depo", to_location_name: "Şube 1",
  lines: [
    { sku: "SKU-001", name: "Örnek Ürün A", unit: "adet", qty_requested: 10, qty_shipped: 10 },
    { sku: "SKU-002", name: "Örnek Ürün B", unit: "koli", qty_requested: 5, qty_shipped: 5 },
  ],
  note: "Örnek sevk notu",
};

// Belge içerik editöründe gerçek belgeyi (örnek verilerle) ölçeklenmiş canlı gösterir.
function LiveDocPreview({ cur, docType, theme, onThemeChange, template, onTemplateChange, customPrimary, setCustomPrimary, customAccent, setCustomAccent }) {
  const iframeRef = useRef(null);
  const [h, setH] = useState(560);
  const [device, setDevice] = useState(docType === "receipt" ? "80mm" : "a5");
  useEffect(() => { setDevice(docType === "receipt" ? "80mm" : "a5"); }, [docType]);
  const deviceEnabled = (d) => (docType === "receipt" ? d !== "a5" : d === "a5");
  const { srcDoc, width } = useMemo(() => {
    const thermal = () => renderToStaticMarkup(<ThermalDoc doc={{ type: "sale", sale: SAMPLE_SALE }} docConfig={cur} />);
    let inner = "", css = "", w = 302;
    if (device === "58mm") { inner = thermal(); css = THERMAL_PREVIEW_CSS; w = 220; }
    else if (device === "80mm") { inner = thermal(); css = THERMAL_PREVIEW_CSS; w = 302; }
    else if (docType === "dispatch") { return { srcDoc: buildDispatchHTML([SAMPLE_ORDER], { docConfig: cur }), width: 559 }; }
    else if (docType === "invoice") { inner = renderToStaticMarkup(<InvoiceA5Doc sale={SAMPLE_SALE} docConfig={cur} />); css = INVOICE_A5_CSS; w = 794; }
    else if (docType === "finance") { inner = renderToStaticMarkup(<FinanceDocA5 entry={SAMPLE_ENTRY} type="makbuz" docConfig={cur} />); css = FINANCE_DOC_CSS; w = 559; }
    else { inner = thermal(); css = THERMAL_PREVIEW_CSS; w = 302; }
    const doc = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><style>*{box-sizing:border-box}body{margin:0;background:#fff;font-family:${docFontFamily()};-webkit-print-color-adjust:exact;print-color-adjust:exact}${css}</style></head><body>${inner}</body></html>`;
    return { srcDoc: doc, width: w };
    // template/customPrimary/customAccent bağımlılıkları kasıtlı: belge bileşenleri
    // temayı/şablonu localStorage'dan okur; değişince önizlemenin yenilenmesi için gerekli.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, docType, device, theme, template, customPrimary, customAccent]);
  const boxW = 288;
  const scale = boxW / width;
  const onLoad = () => {
    try {
      const b = iframeRef.current?.contentDocument?.body;
      if (b) setH(Math.max(120, b.scrollHeight));
    } catch (e) { /* same-origin, güvenli */ }
  };
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Canlı Önizleme</p>
        <div className="inline-flex rounded-md bg-slate-100 p-0.5" data-testid="doc-preview-device">
          {[["58mm", "58mm"], ["80mm", "80mm"], ["a5", "A5"]].map(([k, l]) => {
            const on = deviceEnabled(k);
            return (
              <button key={k} data-testid={`doc-preview-device-${k}`} disabled={!on} onClick={() => on && setDevice(k)}
                title={on ? "" : (docType === "receipt" ? "Fiş A4/A5 basılmaz" : (docType === "invoice" ? "Fatura A4 basılır" : "Bu belge yalnızca A5 basılır"))}
                className={`rounded-sm px-2.5 py-1 text-[11px] font-semibold transition-colors ${device === k ? "bg-white text-brand-green shadow-sm" : on ? "text-slate-500 hover:text-slate-700" : "cursor-not-allowed text-slate-300"}`}>
                {docType === "invoice" && k === "a5" ? "A4" : l}
              </button>
            );
          })}
        </div>
      </div>
      {onThemeChange && (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50/70 p-2.5" data-testid="doc-preview-style">
          <div className="flex items-center gap-2" data-testid="doc-preview-theme">
            <span className="w-16 shrink-0 text-[10px] font-bold uppercase leading-tight tracking-wide text-slate-400">Renk Teması</span>
            <div className="flex flex-1 items-center gap-1 overflow-x-auto pb-1">
              {Object.entries(DOC_THEMES).map(([key, th]) => (
                <button key={key} type="button" data-testid={`doc-preview-theme-${key}`} title={th.name} onClick={() => onThemeChange(key)}
                  className={`h-6 w-6 shrink-0 rounded-full ring-2 transition-transform ${theme === key ? "scale-110 ring-brand-green" : "ring-transparent hover:scale-105"}`}
                  style={{ background: `linear-gradient(135deg, ${th.primary} 55%, ${th.accent} 55%)` }} />
              ))}
              <button type="button" data-testid="doc-preview-theme-custom" title="Özel Renkler" onClick={() => onThemeChange("custom")}
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-400 text-[11px] font-bold text-slate-500 ring-2 transition-transform ${theme === "custom" ? "scale-110 ring-brand-green" : "ring-transparent hover:scale-105"}`}>+</button>
            </div>
          </div>
          <p className="pl-[72px] text-[10px] font-semibold text-slate-500" data-testid="doc-preview-theme-name">{theme === "custom" ? "Özel Renkler" : (DOC_THEMES[theme]?.name || theme)}</p>
          {theme === "custom" && setCustomPrimary && (
            <div className="flex items-center gap-2 pl-[72px]" data-testid="doc-preview-custom-colors">
              <input type="color" data-testid="doc-preview-custom-primary" value={customPrimary || "#064E3B"} onChange={(e) => setCustomPrimary(e.target.value)} className="h-7 w-9 cursor-pointer rounded border border-slate-300 bg-white p-0.5" />
              <input type="color" data-testid="doc-preview-custom-accent" value={customAccent || "#EA580C"} onChange={(e) => setCustomAccent(e.target.value)} className="h-7 w-9 cursor-pointer rounded border border-slate-300 bg-white p-0.5" />
              <span className="text-[10px] text-slate-400">Ana / Vurgu renk</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-[10px] font-bold uppercase leading-tight tracking-wide text-slate-400">Yerleşim Şablonu</span>
            <select data-testid="doc-preview-template" value={template || "klasik"} onChange={(e) => onTemplateChange && onTemplateChange(e.target.value)}
              className="h-8 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 focus:border-brand-green focus:outline-none">
              {Object.entries(DOC_TEMPLATES).map(([key, t]) => <option key={key} value={key}>{t.name}</option>)}
            </select>
          </div>
          <p className="pl-[72px] text-[10px] text-slate-400" data-testid="doc-preview-template-desc">{(DOC_TEMPLATES[template] || {}).desc || ""}</p>
        </div>
      )}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-2 shadow-sm" style={{ width: boxW + 16 }}>
        <div style={{ width: boxW, height: h * scale, overflow: "hidden" }} data-testid="doc-content-live-preview">
          <iframe ref={iframeRef} title="Belge canlı önizleme" srcDoc={srcDoc} scrolling="no" onLoad={onLoad} sandbox="allow-same-origin"
            style={{ width, height: h, border: 0, transform: `scale(${scale})`, transformOrigin: "top left", background: "#fff" }} />
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Örnek verilerle canlı önizleme — yaptığınız değişiklikler ve tema anında yansır. "Kaydet" ile tüm baskılara uygulanır.</p>
    </div>
  );
}

// Belge İçeriği editörü — Ayarlar > Tasarım > Belge Tasarımı altında (Settings.js'ten ayrıldı).
export default function DocContentEditor({ docContent, setDocContent, docTheme, onThemeChange, docTemplate, onTemplateChange, docCustomPrimary, setDocCustomPrimary, docCustomAccent, setDocCustomAccent, saving, onSave, brandStamp, brandStampPos }) {
  const [docType, setDocType] = useState("receipt");
  const [docSavedAt, setDocSavedAt] = useState(null);

  const curDoc = () => (docContent && docContent[docType]) || DOC_CONTENT_DEFAULTS[docType];
  const setDoc = (patch) => { setDocSavedAt(null); setDocContent((c) => ({ ...(c || {}), [docType]: { ...((c && c[docType]) || DOC_CONTENT_DEFAULTS[docType]), ...patch } })); };
  const resetDoc = () => setDoc({ ...DOC_CONTENT_DEFAULTS[docType] });
  const handleThemeChange = (key) => {
    setDocSavedAt(null);
    if (key === "custom") { try { localStorage.setItem("groot_doc_custom", JSON.stringify({ primary: docCustomPrimary || "#064E3B", accent: docCustomAccent || "#EA580C" })); } catch (e) {} }
    onThemeChange(key);
  };
  const handleTemplateChange = (key) => { setDocSavedAt(null); if (onTemplateChange) onTemplateChange(key); };
  const handleCustomPrimary = (v) => { setDocSavedAt(null); if (setDocCustomPrimary) setDocCustomPrimary(v); try { const c = JSON.parse(localStorage.getItem("groot_doc_custom") || "{}") || {}; c.primary = v; c.accent = c.accent || docCustomAccent || "#EA580C"; localStorage.setItem("groot_doc_custom", JSON.stringify(c)); } catch (e) {} };
  const handleCustomAccent = (v) => { setDocSavedAt(null); if (setDocCustomAccent) setDocCustomAccent(v); try { const c = JSON.parse(localStorage.getItem("groot_doc_custom") || "{}") || {}; c.accent = v; c.primary = c.primary || docCustomPrimary || "#064E3B"; localStorage.setItem("groot_doc_custom", JSON.stringify(c)); } catch (e) {} };
  const handleSave = async () => { const ok = await onSave(); if (ok) setDocSavedAt(Date.now()); };
  const copyDocTo = (targets) => {
    const cur = curDoc();
    setDocContent((c) => {
      const nc = { ...(c || {}) };
      targets.forEach((t) => { nc[t] = { ...cur }; });
      return nc;
    });
    toast.success("Ayarlar kopyalandı — kaydetmeyi unutmayın");
  };
  const openPreview = () => {
    const cur = curDoc();
    const w = window.open("", "_blank", "width=580,height=840");
    if (!w) { toast.error("Açılır pencere engellendi — tarayıcı ayarından izin verin"); return; }
    if (docType === "dispatch") {
      const html = buildDispatchHTML([SAMPLE_ORDER], { docConfig: cur });
      w.document.open(); w.document.write(html); w.document.close(); w.focus();
      return;
    }
    let inner = "", css = "", size = docType === "invoice" ? "A4" : "A5";
    if (docType === "invoice") { inner = renderToStaticMarkup(<InvoiceA5Doc sale={SAMPLE_SALE} docConfig={cur} />); css = INVOICE_A5_CSS; }
    else if (docType === "finance") { inner = renderToStaticMarkup(<FinanceDocA5 entry={SAMPLE_ENTRY} type="makbuz" docConfig={cur} />); css = FINANCE_DOC_CSS; }
    else { inner = renderToStaticMarkup(<ThermalDoc doc={{ type: "sale", sale: SAMPLE_SALE }} docConfig={cur} />); css = THERMAL_PREVIEW_CSS; size = "80mm auto"; }
    w.document.open();
    w.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>Belge Önizleme</title><style>@page{size:${size};margin:8mm}body{margin:0;background:#f1f5f9;padding:0;font-family:${docFontFamily()};-webkit-print-color-adjust:exact;print-color-adjust:exact}.pvbar{position:sticky;top:0;background:#064E3B;color:#fff;padding:9px 14px;font-size:13px;display:flex;justify-content:space-between;align-items:center}.pvbar button{background:#EA580C;color:#fff;border:0;border-radius:6px;padding:6px 16px;font-weight:700;cursor:pointer}.pvwrap{background:#fff;margin:16px auto;box-shadow:0 6px 28px rgba(0,0,0,.14);width:max-content;max-width:96%}@media print{.pvbar{display:none}body{background:#fff}.pvwrap{box-shadow:none;margin:0}}${css}</style></head><body><div class="pvbar"><span>Örnek belge önizlemesi — taslak</span><button onclick="window.print()">Yazdır</button></div><div class="pvwrap">${inner}</div></body></html>`);
    w.document.close(); w.focus();
  };

  const cur = curDoc();
  const hl = (cur.header_lines || []).join("\n");
  const fl = (cur.footer_lines || []).join("\n");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-slate-800"><Receipt className="h-5 w-5 text-brand-green" /> Belge İçeriği — Kendin Tasarla</h2>
      <p className="mb-4 text-sm text-slate-500">Renk temasını ve yerleşim şablonunu sağdaki <b>canlı önizlemeden</b> seçin; her belge türü için logo/marka görünürlüğü, başlık, üst/alt satırlar ve QR kodlarını düzenleyip anında görün. Tek "Kaydet" tema + şablon + içeriği tüm baskılara uygular. <b>Klasik</b> varsayılan tasarımdır ve asla bozulmaz.</p>
      <div className="mb-5 flex flex-wrap gap-2" data-testid="doc-content-types">
        {DOC_TYPE_META.map(([k, l]) => (
          <button key={k} type="button" data-testid={`doc-content-type-${k}`} onClick={() => setDocType(k)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${docType === k ? "border-brand-green bg-brand-green text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-sm font-medium text-slate-700">Logoyu göster</span>
            <Switch data-testid="doc-content-show-logo" checked={cur.show_logo} onCheckedChange={(v) => setDoc({ show_logo: v })} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-sm font-medium text-slate-700">Logonun altında marka yazısını göster</span>
            <Switch data-testid="doc-content-show-brand" checked={cur.show_brand} onCheckedChange={(v) => setDoc({ show_brand: v })} />
          </div>
          <div>
            <Label>Belge Başlığı (opsiyonel)</Label>
            <Input data-testid="doc-content-title" value={cur.title} maxLength={60} onChange={(e) => setDoc({ title: e.target.value })} placeholder={`Boş = "${DOC_DEFAULT_TITLE[docType]}"`} className="mt-1" />
          </div>
          <div>
            <Label>Üst Ek Satırlar (her satır ayrı)</Label>
            <textarea data-testid="doc-content-header" value={hl} onChange={(e) => setDoc({ header_lines: e.target.value.split("\n") })} rows={3}
              placeholder="Örn: Müşteri Hizmetleri: 0850 000 00 00" className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm focus:border-brand-green focus:outline-none" />
          </div>
          <div>
            <Label>Alt Bilgi Satırları (her satır ayrı)</Label>
            <textarea data-testid="doc-content-footer" value={fl} onChange={(e) => setDoc({ footer_lines: e.target.value.split("\n") })} rows={3}
              placeholder="Örn: İade koşulları için fişinizi saklayınız." className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm focus:border-brand-green focus:outline-none" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <span className="text-sm font-medium text-slate-700">Doğrulama QR kodu (Belge Doğrula)</span>
            <Switch data-testid="doc-content-qr-verify" checked={cur.qr_verify} onCheckedChange={(v) => setDoc({ qr_verify: v })} />
          </div>
          {(docType === "receipt" || docType === "invoice") && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <span className="text-sm font-medium text-slate-700">Anket / Değerlendirme QR kodu</span>
              <Switch data-testid="doc-content-qr-survey" checked={cur.qr_survey} onCheckedChange={(v) => setDoc({ qr_survey: v })} />
            </div>
          )}
          <div className="rounded-lg border border-slate-200 px-3 py-2" data-testid="doc-content-stamp-image">
            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="text-sm font-medium text-slate-700">Kaşe &amp; İmzayı bu belgede göster</span>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {brandStamp
                    ? "Görsel Marka & Logo'dan gelir; buradan yalnızca bu belge türünde aç/kapatırsınız."
                    : "Görsel yüklü değil — açıkken otomatik yazılı \"RESMÎ BELGE\" mührü basılır. İsterseniz Marka & Logo'dan kendi görselinizi yükleyebilirsiniz."}
                </p>
              </div>
              <Switch data-testid="doc-content-stamp" checked={cur.stamp} onCheckedChange={(v) => setDoc({ stamp: v })} />
            </div>
            {cur.stamp && (
              <div className="mt-2 flex items-center gap-2 border-t border-slate-100 pt-2">
                {brandStamp ? (
                  <>
                    <img src={fileUrl(brandStamp)} alt="Kaşe & İmza" data-testid="doc-content-stamp-preview" className="h-12 w-auto max-w-[110px] rounded border border-slate-200 bg-white object-contain p-1" />
                    <span className="text-[11px] text-slate-400">Konum: {brandStampPos === "top" ? "Yukarı" : brandStampPos === "left" ? "Sol" : brandStampPos === "right" ? "Sağ" : "Orta"} · Belge Tasarımı'ndan yönetilir</span>
                  </>
                ) : (
                  <>
                    <span data-testid="doc-content-stamp-text-preview" className="inline-block -rotate-3 rounded-md border-2 border-brand-green px-3 py-1 text-center text-[10px] font-extrabold leading-tight tracking-wider text-brand-green">RESMÎ BELGE<span className="block text-[8px] font-bold">{docBrandName()}</span></span>
                    <span className="text-[11px] text-slate-400">Otomatik yazılı mühür (görsel yüklenmedi)</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <LiveDocPreview cur={cur} docType={docType} theme={docTheme} onThemeChange={handleThemeChange}
          template={docTemplate} onTemplateChange={handleTemplateChange}
          customPrimary={docCustomPrimary} setCustomPrimary={handleCustomPrimary}
          customAccent={docCustomAccent} setCustomAccent={handleCustomAccent} />
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button data-testid="doc-content-save" onClick={handleSave} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
        </Button>
        {docSavedAt && (
          <span data-testid="doc-content-saved" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5" /> Kaydedildi — önizlemede görüyorsunuz
          </span>
        )}
        <Button data-testid="doc-content-preview" variant="outline" onClick={openPreview} className="border-brand-green text-brand-green hover:bg-emerald-50">
          <Eye className="mr-1.5 h-4 w-4" /> Gerçek Baskı Önizleme
        </Button>
        <button data-testid="doc-content-reset" onClick={resetDoc} className="text-xs font-medium text-red-500 hover:underline">Bu belgeyi varsayılana döndür</button>
      </div>
    </div>
  );
}
